import { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import styles from '../styles/pages/Contact.module.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [acceptRGPD, setAcceptRGPD] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Format du nom et prénom
    if (name === 'lastName' || name === 'firstName') {
      // Supprime les chiffres
      const noDigits = value.replace(/\d/g, '');

      // Supprime les caractères spéciaux sauf -
      const noSpecialChars = noDigits.replace(/[^a-zA-Z\s-]/g, '');

      // Supprime les espaces multiples
      const noExtraSpaces = noSpecialChars.replace(/\s+/g, ' ');

      // Met la première lettre en majuscule et les autres en minuscule
      const capitalized = noExtraSpaces.replace(/\b\w/g, (char) => char.toUpperCase());
      setFormData(prev => ({
        ...prev,
        [name]: capitalized
      }));
      return;
    }
    
    // Format du numéro de téléphone
    if (name === 'phone') {
      // Supprime tous les caractères non numériques
      const digits = value.replace(/\D/g, '');
      
      // Limite à 10 chiffres
      const limitedDigits = digits.slice(0, 10);
      
      // Format avec des points: 06.12.34.56.78
      let formatted = '';
      for (let i = 0; i < limitedDigits.length; i++) {
        if (i > 0 && i % 2 === 0) {
          formatted += '.';
        }
        formatted += limitedDigits[i];
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validations basiques
    // if (!formData.lastName || !formData.firstName || !formData.email || !formData.message) {
    //   setFormStatus({ type: 'error', message: 'Veuillez remplir tous les champs obligatoires.' });
    //   return;
    // }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus({ type: 'error', message: 'Veuillez entrer une adresse email valide.' });
      return;
    }

    // Simulation de soumission du formulaire
    setFormStatus({ type: 'success', message: 'Votre message a été envoyé avec succès! Nous vous répondrons dans les plus brefs délais.' });
    
    // Reset du formulaire
    setFormData({
      lastName: '',
      firstName: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });

    // Suppression du message après 10 secondes
    setTimeout(() => {
      setFormStatus({ type: '', message: '' });
    }, 10000);
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      title: 'Email',
      content: 'contact@meca-gtech.fr',
      link: 'mailto:contact@meca-gtech.fr'
    },
    {
      icon: <Phone size={24} />,
      title: 'Téléphone',
      content: '+33 3 28 62 80 01',
      link: 'tel:+33 3 28 62 80 01'
    },
    {
      icon: <MapPin size={24} />,
      title: 'Adresse',
      content: '25 Ter, Chem. de la Distillerie, 59380 Steene',
      link: 'https://maps.app.goo.gl/NgXaCdnYJatTQT126'
    },
    {
      icon: <Clock size={24} />,
      title: 'Horaires',
      content: 'Lun - Ven: 8h30 - 18h',
      link: null
    }
  ];

  return (
    <div style={{ background: '#f8f9fa' }}>
      {/* Header Section */}
      <section className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>
            Contactez-nous
          </h1>
          <p className={styles.subtitle}>
            Une question ? Notre équipe est prête à vous aider pour tout besoin en maintenance et réparation de moteurs diesel industriels.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section style={{
        maxWidth: '1200px',
        margin: '1rem auto 60px',
        padding: '0 20px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px'
        }}>
          {contactInfo.map((info, index) => (
            <div key={index} style={{
              background: '#fff',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              textAlign: 'center',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              cursor: info.link ? 'pointer' : 'default'
            }}
            className="contact-card"
            onClick={() => info.link && window.open(info.link, '_self')}
            >
              <div style={{
                color: '#1E61A0',
                marginBottom: '15px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                {info.icon}
              </div>
              <h3 className={styles.infoCardTitle}>
                {info.title}
              </h3>
              <p style={{
                color: '#666',
                fontSize: '0.95rem',
                margin: 0,
                fontWeight: '200'
              }} 
              className="links">
                {info.content}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Main Contact Section */}
      <section style={{
        maxWidth: '1200px',
        margin: '0 auto 80px',
        padding: '0 20px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '40px'
        }}>
          {/* Contact Form */}
          <div className={styles.formSection} id='formulaire'>
            <h2 className={styles.formTitle}>
              Envoyez-nous un message
            </h2>
            <p style={{
              color: 'red',
              marginBottom: '30px'
            }}>
              Formulaire indisponible pour le moment.
            </p>

            {formStatus.message && (
              <div className={styles.statusMessage}>
                {formStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px', width: '40%', float: 'left' }}>
                <label className={styles.formLabel}>
                  Nom<span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled
                  placeholder="Dupont"
                  className="form-control"
                  onFocus={(e) => e.target.style.borderColor = '#61dafb'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '20px', width: '40%', float: 'right' }}>
                <label className={styles.formLabel}>
                  Prénom<span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled
                  placeholder="Jean"
                  className="form-control"
                  onFocus={(e) => e.target.style.borderColor = '#61dafb'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '20px', width: '100%', float: 'left' }}>
                <label className={styles.formLabel}>
                  Email <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled
                  placeholder="jean.dupont@mail.com"
                  className="form-control"
                  onFocus={(e) => e.target.style.borderColor = '#61dafb'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>


              <div style={{ marginBottom: '20px', width: '100%', float: 'left' }}>
                <label className={styles.formLabel}>
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="06.12.34.56.78"
                  className="form-control"
                  disabled
                  onFocus={(e) => e.target.style.borderColor = '#61dafb'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '20px', width: '100%', float: 'left' }}>
                <label className={styles.formLabel}>
                  Sujet
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Diagnostic, Réparation, Usinage, Autre..."
                  className="form-control"
                  disabled
                  onFocus={(e) => e.target.style.borderColor = '#61dafb'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '20px', width: '100%', float: 'left' }}>
                <label className={styles.formLabel}>
                  Message <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled
                  rows="6"
                  placeholder="Décrivez-nous votre besoin"
                  className={styles.formTextarea}
                  onFocus={(e) => e.target.style.borderColor = '#61dafb'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              {/* Case à cocher RGPD */}
              <div style={{ marginBottom: '20px', width: '100%', float: 'left' }}>
                <label className={styles.rgpdCheckbox}>
                  <input
                    type="checkbox"
                    checked={acceptRGPD}
                    onChange={(e) => setAcceptRGPD(e.target.checked)}
                    required
                    disabled
                  />
                  <span>
                    J'accepte que mes données personnelles soient utilisées pour répondre à ma demande de contact. 
                    Les données sont conservées pendant 3 ans maximum. 
                    <Link to="/legale-notice" 
                      style={{ 
                        color: '#011F37', 
                        textDecoration: 'underline',
                        marginLeft: '4px'
                      }}
                    >
                      En savoir plus sur la protection de vos données
                    </Link>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '14px',
                  background: acceptRGPD ? 'linear-gradient(135deg, #011F37 0%, #013a5f 100%)' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: acceptRGPD ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  opacity: acceptRGPD ? 1 : 0.6,
                  transition: 'all 0.3s ease'
                }}
                disabled={!acceptRGPD || true}
                // onMouseOver={(e) => {
                //   e.target.style.transform = 'translateY(-2px)';
                //   e.target.style.boxShadow = '0 6px 12px rgba(1, 31, 55, 0.3)';
                // }}
                // onMouseOut={(e) => {
                //   e.target.style.transform = 'translateY(0)';
                //   e.target.style.boxShadow = 'none';
                // }}

                // Envoyer le message au serveur OVH
                onClick={() => {
                  
                }}
              >
                <Send size={20} />
                Envoyer le message
              </button>
            </form>
          </div>

          {/* Informations supplémentaires */}
          <div>
            {/* Pourquoi nous contacter ? */}
            <div className={styles.infoCard}>
              <h2 className={styles.infoCardTitle} style={{
                fontSize: '2rem'
              }}>
                Pourquoi nous contacter ?
              </h2>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Demande de devis personnalisé',
                  'Information sur nos services',
                  'Support technique',
                  'Partenariats et collaborations',
                  'Questions générales'
                ].map((item, index) => (
                  <li key={index} style={{
                    padding: '12px 0',
                    borderBottom: index < 4 ? '1px solid #e0e0e0' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: '#333'
                  }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      background: '#676768',
                      borderRadius: '50%',
                      flexShrink: 0
                    }}></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Social Media */}
            <div style={{
              background: 'linear-gradient(135deg, #011F37 0%, #013a5f 100%)',
              padding: '40px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              color: '#fff',
              textAlign: 'center'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '15px'
              }}>
                Suivez-nous
              </h3>
              <p style={{
                opacity: '0.9',
                marginBottom: '25px'
              }}>
                Restez informé de nos dernières actualités
              </p>
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '15px'
              }}>
                <a
                  href="https://www.linkedin.com/company/meca-gtech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors"
                >
                  <Linkedin size={24} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
